import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    Gift, Copy, Check, Loader2, Users, Sparkles, ExternalLink,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const EmployerReferrals = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/employer/referrals");
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const shareLink = data
        ? `${window.location.origin}/signup?role=employer&ref=${data.referral_code}`
        : "";

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareLink);
            setCopied(true);
            toast.success("Link copied!");
            setTimeout(() => setCopied(false), 2000);
        } catch { toast.error("Couldn't copy — please copy manually."); }
    };

    if (loading || !data) return <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>;

    return (
        <div data-testid="employer-referrals-page" className="space-y-6">
            <div>
                <p className="overline">Grow together</p>
                <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Refer &amp; earn</h1>
                <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                    Share Simonara with a peer employer. When they sign up with your link and publish their first role, you get a <strong className="text-foreground">free 7-day Featured Job promotion</strong> — a $49 value.
                </p>
            </div>

            {/* Credits + link */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="card-soft p-5 bg-gradient-to-br from-amber-50 to-white dark:from-amber-500/10 dark:to-transparent">
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                        <Gift size={14} /> <p className="overline">Free Featured credits</p>
                    </div>
                    <div className="mt-3 text-4xl font-heading font-semibold text-foreground" data-testid="referrals-credits">{data.credits}</div>
                    <p className="mt-1 text-xs text-muted-foreground">Redeem on any published job — one credit = 7 days of top-of-board placement.</p>
                    <Link to="/employer/jobs" className="mt-4 inline-flex text-xs font-semibold text-brand-green hover:underline">Apply a credit →</Link>
                </motion.div>

                <div className="card-soft p-5 md:col-span-2">
                    <p className="overline">Your shareable link</p>
                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm">
                        <code data-testid="referrals-link" className="flex-1 truncate text-foreground">{shareLink}</code>
                        <button
                            onClick={copy}
                            data-testid="referrals-copy"
                            className="inline-flex items-center gap-1 rounded-full bg-brand-green text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90"
                        >
                            {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                        </button>
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">
                        Or share your code: <code className="bg-muted px-1.5 py-0.5 rounded text-foreground font-semibold">{data.referral_code}</code>
                    </p>
                </div>
            </div>

            {/* Referred list */}
            <section>
                <h2 className="font-heading font-semibold text-lg text-foreground inline-flex items-center gap-2">
                    <Users size={14} className="text-emerald-500" /> People you&apos;ve referred
                </h2>
                {data.referred.length === 0 ? (
                    <div data-testid="referrals-empty" className="card-soft p-8 text-center mt-3">
                        <Sparkles size={20} className="mx-auto text-emerald-500" />
                        <p className="mt-2 text-sm font-semibold text-foreground">Nobody yet — send that link!</p>
                        <p className="text-xs text-muted-foreground mt-1">Ping it in a Discord server, drop it in a HR community, or add it to your email signature.</p>
                    </div>
                ) : (
                    <div className="mt-3 card-soft overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 text-left">Company</th>
                                    <th className="px-4 py-3 text-left">Email</th>
                                    <th className="px-4 py-3 text-left">Signed up</th>
                                    <th className="px-4 py-3 text-left">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.referred.map((r, i) => (
                                    <tr key={i} data-testid={`referral-row-${i}`} className="border-t border-border">
                                        <td className="px-4 py-3 text-foreground">{r.company || "—"}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{r.email}</td>
                                        <td className="px-4 py-3 text-muted-foreground text-xs">{r.signed_up_at ? new Date(r.signed_up_at).toLocaleDateString() : "—"}</td>
                                        <td className="px-4 py-3">
                                            {r.credited ? (
                                                <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-0 text-[10px]">Credit earned</Badge>
                                            ) : (
                                                <Badge variant="outline" className="text-[10px]">Awaiting first job</Badge>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

export default EmployerReferrals;
